import type { StorageData } from "../types";
import type { Transport } from "../transport";

/**
 * Облачные сохранения. Размер: до 100 KB на игру для бесплатных юзеров,
 * до 10 MB для Premium.
 *
 * Поведение для гостей: используется `localStorage` браузера. Прогресс
 * сохраняется локально и переедет в облако при первом логине.
 *
 * Для авторизованных: save/load через backend (защищено от накруток).
 *
 * Обработка ошибок:
 *   ```ts
 *   try {
 *     await IgrayTut.storage.save({ level: 5, coins: 1000 });
 *   } catch (e) {
 *     if (e instanceof IgrayTut.SDKError) {
 *       if (e.code === "quota_exceeded") showPremiumPrompt();
 *       else if (e.code === "rate_limit_exceeded") setTimeout(retry, e.retryAfterMs);
 *     }
 *   }
 *   ```
 *
 * Коды ошибок: `quota_exceeded`, `rate_limit_exceeded`, `guest_not_supported`,
 * `unknown_game`, `network_error`. См. `SDK_ERROR_CODES`.
 */
export function createStorage(t: Transport) {
  return {
    /**
     * Сохранить прогресс. Перезаписывает всё что было.
     * Если нужна частичная запись — сначала load(), смерджи, save().
     *
     * При переполнении квоты бросает `SDKError` с `code === "quota_exceeded"`.
     */
    save(data: StorageData): Promise<void> {
      return t.request<void>("storage.save", data);
    },

    /**
     * Загрузить сохранение. Возвращает null если нет.
     */
    load(): Promise<StorageData | null> {
      return t.request<StorageData | null>("storage.load");
    },

    /**
     * Удалить сохранение. Необратимо.
     */
    remove(): Promise<void> {
      return t.request<void>("storage.remove");
    },

    /**
     * Проверка квоты до попытки сохранения. Wave 7 A2.
     *
     * Возвращает:
     *   - `used` — сколько байт занимает текущее сохранение
     *   - `limit` — общий лимит (100 КБ / 10 МБ)
     *   - `remaining` — сколько ещё можно записать до переполнения
     *   - `isPremium` — активна ли Premium-подписка у юзера
     *
     * Полезно чтобы игра могла показать юзеру «осталось 20 КБ» или сразу
     * предложить Premium перед сложной операцией сохранения.
     */
    getQuota(): Promise<{
      used: number;
      limit: number;
      remaining: number;
      isPremium: boolean;
    }> {
      return t.request("storage.getQuota");
    },
  };
}

export type StorageApi = ReturnType<typeof createStorage>;
